/**
 * ConversationTimeline
 *
 * Renders a list of interactions, grouping the legs of ONE conversation together.
 *
 * A transferred call is one conversation split across two recordings: the first person
 * answers, talks, then transfers. Each leg has its own recording_id (the pipeline's dedup
 * key), so the rows stay separate in the database - merging them would orphan a recording and
 * let the next webhook recreate it. They are LINKED by `conversation_key` and merged HERE,
 * for reading only.
 *
 * A single-leg conversation renders exactly as a bare card - no group chrome, because there
 * is no group.
 *
 * Inline style objects with plain elements: the same component has to render inside the MUI
 * CRM page and the plain-CSS Bridge Club page.
 */

// ==== STANDALONE:FARMHAND_PRO:START ====
// Feature: conversation-level grouping of call legs on a CRM timeline.
// Tier: Pro (telephony)
// Dependencies: components/InteractionCard, services/customerCrmAPI
// ============================================

import React, { useState } from 'react';
import { CustomerInteraction } from '../services/customerCrmAPI';
import InteractionCard from './InteractionCard';

const C = {
  text: '#111827',
  muted: '#6b7280',
  border: '#e5e7eb',
  subtle: '#f9fafb',
  primary: '#059669',
  primarySoft: '#ecfdf5',
};

interface Props {
  interactions: CustomerInteraction[];
  /** Label the first entry, e.g. "Last Conversation". */
  labelFirst?: string;
  /** Label the second entry (the CRM page shows only two). */
  labelSecond?: string;
}

interface Group {
  key: string;
  legs: CustomerInteraction[];
}

/** Time order within a conversation. leg_started_at is CALL time; interaction_date is write
 *  time, which a retry can invert, so it is only the fallback. */
const legTime = (i: CustomerInteraction): number => {
  const raw = i.leg_started_at || i.interaction_date;
  const t = raw ? new Date(raw).getTime() : NaN;
  return isNaN(t) ? 0 : t;
};

/** Group consecutive-or-not legs by conversation_key, preserving first-appearance order.
 *  A blank key is always its own group - never collapse unlinked rows together. */
export const groupByConversation = (interactions: CustomerInteraction[]): Group[] => {
  const groups: Group[] = [];
  const byKey = new Map<string, Group>();

  interactions.forEach((i, idx) => {
    const key = (i.conversation_key || '').trim();
    if (!key) {
      groups.push({ key: `__solo_${i.id}_${idx}`, legs: [i] });
      return;
    }
    const existing = byKey.get(key);
    if (existing) {
      existing.legs.push(i);
    } else {
      const g: Group = { key, legs: [i] };
      byKey.set(key, g);
      groups.push(g);
    }
  });

  groups.forEach((g) => g.legs.sort((a, b) => legTime(a) - legTime(b)));
  return groups;
};

const formatTotal = (seconds: number): string => {
  if (seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const ConversationGroup: React.FC<{ legs: CustomerInteraction[]; label?: string }> = ({
  legs, label,
}) => {
  const [showCombined, setShowCombined] = useState(false);

  const totalSeconds = legs.reduce((sum, l) => sum + (l.duration_seconds || 0), 0);

  // Concatenate in leg order. Prefer the labeled transcript, fall back to the raw one -
  // same precedence the individual card uses.
  const combined = legs
    .map((l, idx) => {
      const text = (l.transcript_labeled || l.transcription || '').trim();
      if (!text) return '';
      return `── Leg ${idx + 1} ──\n${text}`;
    })
    .filter(Boolean)
    .join('\n\n');

  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderLeft: `4px solid ${C.primary}`,
        borderRadius: 8,
        padding: 10,
        marginBottom: 12,
        backgroundColor: C.subtle,
      }}
    >
      {label && (
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
          {label}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: C.text }}>
          One conversation, {legs.length} legs
        </span>
        <span style={{ fontSize: '0.78rem', color: C.muted }}>
          transferred{totalSeconds > 0 ? ` · ${formatTotal(totalSeconds)} total` : ''}
        </span>
      </div>

      {legs.map((leg, idx) => (
        <InteractionCard
          key={leg.id}
          interaction={leg}
          highlight={false}
          label={`Leg ${idx + 1} of ${legs.length}`}
        />
      ))}

      {combined && (
        <>
          <button
            type="button"
            onClick={() => setShowCombined((v) => !v)}
            style={{
              width: '100%',
              minHeight: 44,
              padding: '10px 12px',
              textAlign: 'left',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: C.text,
              backgroundColor: '#ffffff',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            {showCombined ? 'Hide combined transcript ▲' : 'Show combined transcript ▼'}
            <span style={{ fontWeight: 400, color: C.muted }}> (all legs in order)</span>
          </button>
          {showCombined && (
            <div
              style={{
                marginTop: 8,
                padding: 10,
                maxHeight: 360,
                overflowY: 'auto',
                overflowX: 'auto',
                backgroundColor: '#ffffff',
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: '0.82rem',
                lineHeight: 1.55,
                color: C.text,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              {combined}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ConversationTimeline: React.FC<Props> = ({ interactions, labelFirst, labelSecond }) => {
  const groups = groupByConversation(interactions);

  return (
    <>
      {groups.map((group, idx) => {
        const label = idx === 0 ? labelFirst : idx === 1 ? labelSecond : undefined;

        if (group.legs.length === 1) {
          return (
            <InteractionCard
              key={group.legs[0].id}
              interaction={group.legs[0]}
              highlight={idx === 0}
              label={label}
            />
          );
        }
        return <ConversationGroup key={group.key} legs={group.legs} label={label} />;
      })}
    </>
  );
};

export default ConversationTimeline;
// ==== STANDALONE:FARMHAND_PRO:END ====
